import type { Product, Review, AggregateRating } from 'schema-dts'

export interface JsonLdProductReviewFragment {
  author_name: string
  rating_value: number
  best_rating?: number
  review_body: string
  date_published?: string
}

export interface JsonLdProductAggregateRatingFragment {
  rating_value: number
  review_count: number
  best_rating?: number
}

/** @see https://developers.google.com/search/docs/advanced/structured-data/product#review */
export function jsonLdProductReview(props: JsonLdProductReviewFragment): Pick<Product, 'review'> {
  const { author_name, rating_value, best_rating = 5, review_body, date_published } = props

  return {
    review: {
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: author_name,
      },
      reviewRating: {
        '@type': 'Rating',
        ratingValue: rating_value,
        bestRating: best_rating,
      },
      reviewBody: review_body,
      datePublished: date_published ?? undefined,
    },
  }
}

/** @see https://developers.google.com/search/docs/advanced/structured-data/product#aggregaterating */
export function jsonLdProductAggregateRating(
  props: JsonLdProductAggregateRatingFragment,
): Pick<Product, 'aggregateRating'> {
  const { rating_value, review_count, best_rating = 5 } = props

  return {
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: rating_value,
      reviewCount: review_count,
      bestRating: best_rating,
    },
  }
}

/** @see https://developers.google.com/search/docs/advanced/structured-data/product#review */
export function jsonLdProductMultipleReviews(
  reviews: JsonLdProductReviewFragment[],
): Pick<Product, 'review'> {
  return {
    review: reviews?.map((review) => ({
      '@type': 'Review' as const,
      author: {
        '@type': 'Person' as const,
        name: review.author_name,
      },
      reviewRating: {
        '@type': 'Rating' as const,
        ratingValue: review.rating_value,
        bestRating: review.best_rating ?? 5,
      },
      reviewBody: review.review_body,
      datePublished: review.date_published ?? undefined,
    })),
  }
}
